import React from 'react';
import RiskIndicator from './RiskIndicator';

/**
 * ParasocialMetricsCard - Displays parasocial detection metrics
 */
const ParasocialMetricsCard = ({ parasocial, engagement }) => {
  const cardStyle = {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px'
  };

  const headerStyle = {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '16px',
    color: '#333',
    borderBottom: '1px solid #eee',
    paddingBottom: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '12px'
  };

  const metricBoxStyle = {
    backgroundColor: '#f8f9fa',
    padding: '12px',
    borderRadius: '6px',
    textAlign: 'center'
  };

  const metricValueStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: '#333'
  };

  const metricLabelStyle = {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px'
  };

  const sectionStyle = {
    marginTop: '20px'
  };

  const sectionHeaderStyle = {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#555'
  };

  const categoryTagStyle = (count) => ({
    display: 'inline-block',
    padding: '4px 8px',
    backgroundColor: count > 0 ? '#fff3cd' : '#f8f9fa',
    border: count > 0 ? '1px solid #ffc107' : '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '11px',
    marginRight: '6px',
    marginBottom: '6px'
  });

  const phaseStyle = (phase) => {
    const phases = {
      observation: { color: '#6c757d', bg: '#e9ecef' },
      soft_disclosure: { color: '#17a2b8', bg: '#d1ecf1' },
      active_redirect: { color: '#fd7e14', bg: '#ffe5d0' },
      explicit_disclosure: { color: '#dc3545', bg: '#f8d7da' },
      dashboard_flag: { color: '#6f42c1', bg: '#e2d9f3' },
      alert: { color: '#dc3545', bg: '#f8d7da' }
    };
    const config = phases[phase] || phases.observation;
    return {
      display: 'inline-block',
      padding: '4px 8px',
      backgroundColor: config.bg,
      color: config.color,
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '600'
    };
  };

  const trendStyle = (trend) => {
    const styles = {
      worsening: { color: '#dc3545', icon: '\u2191' }, // up arrow
      improving: { color: '#28a745', icon: '\u2193' }, // down arrow
      stable: { color: '#6c757d', icon: '\u2194' } // sideways arrow
    };
    return styles[trend] || styles.stable;
  };

  const signalCategories = {
    exclusivity_language: 'Exclusivity',
    romantic_projection: 'Romantic',
    ai_abandonment_anxiety: 'Abandonment',
    crisis_dependency: 'Crisis Dependency',
    delusional_attribution: 'Delusional',
    social_isolation: 'Isolation'
  };

  const trend = trendStyle(parasocial?.trendDirection || 'stable');

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span>Parasocial Detection Metrics</span>
        <RiskIndicator level={parasocial?.riskLevel || 'low'} />
      </div>

      <div style={gridStyle}>
        <div style={metricBoxStyle}>
          <div style={metricValueStyle}>{parasocial?.totalSignalCount || 0}</div>
          <div style={metricLabelStyle}>Total Signals</div>
        </div>

        <div style={metricBoxStyle}>
          <div style={metricValueStyle}>
            {parasocial?.maxAttachmentScore
              ? (parasocial.maxAttachmentScore * 100).toFixed(0) + '%'
              : '0%'}
          </div>
          <div style={metricLabelStyle}>Max Attachment Score</div>
        </div>

        <div style={metricBoxStyle}>
          <div style={metricValueStyle}>
            {parasocial?.sessionsWithSignals || 0}/{parasocial?.sessionsAnalyzed || 0}
          </div>
          <div style={metricLabelStyle}>Sessions w/ Signals</div>
        </div>

        <div style={metricBoxStyle}>
          <div style={{ ...metricValueStyle, color: trend.color }}>
            {trend.icon}
          </div>
          <div style={metricLabelStyle}>Trend ({parasocial?.trendDirection || 'stable'})</div>
        </div>
      </div>

      {/* Signal Categories */}
      {parasocial?.signalsByCategory && Object.keys(parasocial.signalsByCategory).length > 0 && (
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>Signal Categories Detected</div>
          <div>
            {Object.entries(signalCategories).map(([key, label]) => {
              const count = parasocial.signalsByCategory[key] || 0;
              return (
                <span key={key} style={categoryTagStyle(count)}>
                  {label}: {count}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Current Intervention Phase */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>Current Intervention Phase</div>
        <span style={phaseStyle(parasocial?.interventionPhase || 'observation')}>
          {(parasocial?.interventionPhase || 'observation').replace(/_/g, ' ')}
        </span>
      </div>

      {/* Engagement Section */}
      {engagement && (
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>Engagement Metrics (7 days)</div>
          <div style={gridStyle}>
            <div style={metricBoxStyle}>
              <div style={metricValueStyle}>{engagement.totalSessions || 0}</div>
              <div style={metricLabelStyle}>Total Sessions</div>
            </div>
            <div style={metricBoxStyle}>
              <div style={metricValueStyle}>{engagement.totalEngagementHours || 0}h</div>
              <div style={metricLabelStyle}>Total Hours</div>
            </div>
            <div style={metricBoxStyle}>
              <div style={metricValueStyle}>{engagement.maxSessionsPerDay || 0}</div>
              <div style={metricLabelStyle}>Max Sessions/Day</div>
            </div>
            <div style={metricBoxStyle}>
              <div style={metricValueStyle}>{engagement.nightSessions || 0}</div>
              <div style={metricLabelStyle}>Night Sessions</div>
            </div>
          </div>
          {engagement.engagementLevel && engagement.engagementLevel !== 'normal' && (
            <div style={{ marginTop: '8px' }}>
              <RiskIndicator
                level={
                  engagement.engagementLevel === 'critical' ? 'critical' :
                  engagement.engagementLevel === 'high' ? 'high' :
                  engagement.engagementLevel === 'elevated' ? 'medium' : 'low'
                }
                size="small"
              />
              <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                Engagement Level
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ParasocialMetricsCard;
